import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, EmptyState, StarRating } from "@/components/shared";
import { ShieldCheck, Check, X, Flag, Scale, ScrollText, Bot, Siren, FileCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

function AiChip({ ai }) {
  if (!ai) return null;
  const risk = Math.max(ai.spam_score || 0, ai.toxicity || 0);
  const cls = risk >= 60 ? "border-coral-500/40 text-coral-500" : risk >= 30 ? "border-info-500/40 text-info-500" : "border-success-500/40 text-success-500";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${cls}`}>
      <Bot className="h-3.5 w-3.5" /> spam {ai.spam_score ?? 0} · toxicity {ai.toxicity ?? 0} · {ai.recommendation}
    </span>
  );
}

export default function Moderation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [queue, setQueue] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    api.get("/moderation/queue").then(({ data }) => setQueue(data)).catch(() => setQueue([]));
    api.get("/moderation/disputes").then(({ data }) => setDisputes(data)).catch(() => {});
    api.get("/moderation/reports").then(({ data }) => setReports(data)).catch(() => {});
    api.get("/moderation/audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/moderation/verifications").then(({ data }) => setVerifs(data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user && user.role !== "admin" && user.role !== "moderator") { nav("/"); return; }
    if (user) load();
    // eslint-disable-next-line
  }, [user]);

  const approve = async (id) => {
    try { await api.post(`/moderation/${id}/approve`); toast.success("Review published"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decline = async () => {
    try { await api.post(`/moderation/${declineFor}/decline`, { reason }); toast.success("Review declined"); setDeclineFor(null); setReason(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveDispute = async (id) => {
    try { await api.post(`/moderation/disputes/${id}/resolve`, { reason: "Reviewed and resolved by moderator" }); toast.success("Dispute resolved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveReport = async (id, action) => {
    try { await api.post(`/moderation/reports/${id}/resolve`, { reason: action }); toast.success(action === "remove" ? "Review removed" : "Report dismissed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decideVerif = async (vid, decision, r = "") => {
    try { await api.post(`/moderation/verifications/${vid}/decision`, { decision, reason: r }); toast.success(decision === "approve" ? "Verification approved" : "Verification rejected"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (user === null || queue === null) return <Loading />;

  return (
    <main className="mx-auto max-w-[1800px] px-6 py-10 sm:px-10 lg:px-16">
      <p className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Moderator console</p>
      <h1 className="text-3xl font-bold">Moderation Queue</h1>

      <Tabs defaultValue="queue" className="mt-8">
        <TabsList className="bg-ink-900">
          <TabsTrigger data-testid="tab-queue" value="queue">Queue ({queue.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-disputes" value="disputes">Disputes ({disputes.filter(d => d.status === "open").length})</TabsTrigger>
          <TabsTrigger data-testid="tab-reports" value="reports">Reports ({reports.filter(r => r.status === "open").length})</TabsTrigger>
          <TabsTrigger data-testid="tab-verifications" value="verifications">Verifications ({verifs.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-audit" value="audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-6 space-y-4">
          {queue.length === 0 ? <EmptyState icon={Check} title="Queue is clear" subtitle="No reviews awaiting moderation." /> :
            queue.map((r) => (
              <div key={r.id} data-testid="queue-item" className="card-surface p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">{r.title}</h3>
                    <p className="mt-1 text-xs text-slate-500">{r.company_name} · {r.job_title || "Anonymous"} · {r.employment_status}</p>
                  </div>
                  <StarRating value={r.rating_overall} />
                </div>
                <div className="mt-3"><AiChip ai={r.ai_moderation} /></div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div><p className="text-xs font-semibold text-success-500">PROS</p><p className="mt-1 text-sm text-slate-300">{r.pros}</p></div>
                  <div><p className="text-xs font-semibold text-coral-500">CONS</p><p className="mt-1 text-sm text-slate-300">{r.cons}</p></div>
                </div>
                {r.red_flags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">{r.red_flags.map(f => <span key={f} className="flex items-center gap-1 rounded-full border border-coral-500/30 bg-coral-500/10 px-2.5 py-1 text-xs text-coral-500"><Flag className="h-3 w-3" />{f}</span>)}</div>
                )}
                <div className="mt-5 flex gap-3">
                  <button data-testid="approve-btn" onClick={() => approve(r.id)} className="btn-teal inline-flex items-center gap-1.5 text-sm"><Check className="h-4 w-4" /> Approve & Publish</button>
                  <Dialog open={declineFor === r.id} onOpenChange={(o) => { setDeclineFor(o ? r.id : null); setReason(""); }}>
                    <DialogTrigger asChild>
                      <button data-testid="decline-btn" className="inline-flex items-center gap-1.5 rounded-full border border-coral-500/40 px-5 py-2.5 text-sm text-coral-500 hover:bg-coral-500/10"><X className="h-4 w-4" /> Decline</button>
                    </DialogTrigger>
                    <DialogContent className="border-[#DDDAD1] bg-ink-900">
                      <DialogHeader><DialogTitle>Decline review</DialogTitle></DialogHeader>
                      <p className="text-sm text-slate-400">The reviewer will see this reason on their dashboard.</p>
                      <Textarea data-testid="decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for declining…" className="border-[#DDDAD1] bg-ink-900" />
                      <DialogFooter><button data-testid="decline-confirm" onClick={decline} disabled={!reason} className="btn-teal text-sm disabled:opacity-50">Confirm decline</button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="disputes" className="mt-6 space-y-4">
          {disputes.length === 0 ? <EmptyState icon={Scale} title="No disputes" subtitle="Employer legal requests will appear here." /> :
            disputes.map((d) => (
              <div key={d.id} data-testid="dispute-item" className="card-surface p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{d.review_title || "Review"} · {d.legal_basis}</h3>
                    <p className="mt-1 text-xs text-slate-500">Filed by {d.employer_name} · {new Date(d.created_at).toLocaleDateString()}</p>
                    <p className="mt-3 text-sm text-slate-300">{d.reason}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs ${d.status === "open" ? "border-info-500/40 text-info-500" : "border-success-500/40 text-success-500"}`}>{d.status}</span>
                </div>
                {d.status === "open" && <button data-testid="resolve-dispute" onClick={() => resolveDispute(d.id)} className="btn-teal mt-4 text-sm">Mark resolved</button>}
              </div>
            ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-4">
          {reports.length === 0 ? <EmptyState icon={Siren} title="No reports" subtitle="Reader reports of reviews will appear here." /> :
            reports.map((rep) => (
              <div key={rep.id} data-testid="report-item" className="card-surface p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{rep.review_title || "Review"}</h3>
                    <p className="mt-1 text-xs text-slate-500">Reported by {rep.reporter} · {new Date(rep.created_at).toLocaleDateString()}</p>
                    <p className="mt-3 text-sm text-coral-500">{rep.reason}</p>
                    {rep.detail && <p className="mt-1 text-sm text-slate-300">{rep.detail}</p>}
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs ${rep.status === "open" ? "border-info-500/40 text-info-500" : "border-teal-400/40 text-teal-300"}`}>{rep.status}</span>
                </div>
                {rep.status === "open" && (
                  <div className="mt-4 flex gap-3">
                    <button data-testid="report-remove" onClick={() => resolveReport(rep.id, "remove")} className="inline-flex items-center gap-1.5 rounded-full border border-coral-500/40 px-5 py-2.5 text-sm text-coral-500 hover:bg-coral-500/10"><X className="h-4 w-4" /> Remove review</button>
                    <button data-testid="report-dismiss" onClick={() => resolveReport(rep.id, "dismiss")} className="inline-flex items-center gap-1.5 rounded-full border border-[#DDDAD1] px-5 py-2.5 text-sm text-slate-300 hover:border-teal-400/40"><Check className="h-4 w-4" /> Dismiss</button>
                  </div>
                )}
              </div>
            ))}
        </TabsContent>

        <TabsContent value="verifications" className="mt-6 space-y-4">
          {verifs.length === 0 ? <EmptyState icon={FileCheck} title="No pending verifications" subtitle="Document and LinkedIn checks awaiting approval appear here." /> :
            verifs.map((v) => (
              <div key={v.id} data-testid="verif-item" className="card-surface p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{v.company_name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{v.user_name} · {v.user_email} · via {v.method}</p>
                    {v.method === "linkedin" && v.linkedin_url && <a href={v.linkedin_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-400 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View LinkedIn profile</a>}
                    {v.method === "linkedin" && v.role_note && <p className="mt-1 text-sm text-slate-300">Role: {v.role_note}</p>}
                    {v.method === "document" && v.has_document && <a href={`${API}/moderation/verifications/${v.id}/document`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-400 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View document ({v.doc_filename})</a>}
                  </div>
                  <span className="rounded-full border border-info-500/40 px-3 py-1 text-xs text-info-500">pending</span>
                </div>
                <div className="mt-4 flex gap-3">
                  <button data-testid="verif-approve" onClick={() => decideVerif(v.id, "approve")} className="btn-teal inline-flex items-center gap-1.5 text-sm"><Check className="h-4 w-4" /> Approve</button>
                  <button data-testid="verif-reject" onClick={() => decideVerif(v.id, "reject", "Could not confirm employment")} className="inline-flex items-center gap-1.5 rounded-full border border-coral-500/40 px-5 py-2.5 text-sm text-coral-500 hover:bg-coral-500/10"><X className="h-4 w-4" /> Reject</button>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="card-surface divide-y divide-[#E4E1D8]">
            {audit.length === 0 ? <EmptyState icon={ScrollText} title="No audit entries" /> :
              audit.map((a) => (
                <div key={a.id} data-testid="audit-item" className="flex items-center justify-between px-5 py-3 text-sm">
                  <span className="text-slate-300"><span className="text-teal-400">{a.action}</span> by {a.actor}</span>
                  <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
